import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { formatEUR, formatDate } from "../lib/format";
import { MagnifyingGlass, Broom } from "@phosphor-icons/react";

const ACTIONS = [
  { value: "delete", label: "Löschen (doppelt)" },
  { value: "apply", label: "Rechnung zuordnen (IST)" },
  { value: "keep", label: "Behalten" },
];

const AuditRow = ({ it, choice, onChange }) => (
  <tr className="border-b border-[#F1F5F9] last:border-0" data-testid={`audit-row-${it.payment_id}`}>
    <td className="px-3 py-2">
      <div className="font-medium text-[#1F242E]">{it.customer_name}</div>
      <div className="text-[10px] text-[#94A3B8]">{it.reason}</div>
    </td>
    <td className="px-3 py-2 text-sm">{formatDate(it.payment_date)}</td>
    <td className="px-3 py-2 text-right font-display font-bold">{formatEUR(it.amount)}</td>
    <td className="px-3 py-2">
      <select className="field !py-1 !text-xs w-full" value={choice.invoice_id || ""}
        onChange={(e) => onChange({ ...choice, invoice_id: e.target.value || null })}
        data-testid={`audit-invoice-${it.payment_id}`}>
        <option value="">— keine —</option>
        {it.candidates.map((c) => (
          <option key={c.invoice_id} value={c.invoice_id}>
            {c.customer_name} · {formatEUR(c.total_amount)} · {c.fully_paid ? "bereits bezahlt" : `offen ${formatEUR(c.open_amount)}`}
          </option>
        ))}
      </select>
    </td>
    <td className="px-3 py-2">
      <select className="field !py-1 !text-xs w-full" value={choice.action}
        onChange={(e) => onChange({ ...choice, action: e.target.value })}
        data-testid={`audit-action-${it.payment_id}`}>
        {ACTIONS.map((a) => (
          <option key={a.value} value={a.value} disabled={a.value === "apply" && !choice.invoice_id}>{a.label}</option>
        ))}
      </select>
    </td>
  </tr>
);

export const PaymentAuditSection = () => {
  const [data, setData] = useState(null);
  const [choices, setChoices] = useState({});
  const [busy, setBusy] = useState(false);

  const check = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/maintenance/payment-audit");
      setData(data);
      setChoices(Object.fromEntries(data.items.map((i) => [i.payment_id, { action: i.suggestion, invoice_id: i.selected_invoice_id }])));
    } catch (e) {
      toast.error("Prüfung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const changes = Object.entries(choices).filter(([, c]) => c.action !== "keep");

  const apply = async () => {
    if (!changes.length) return;
    if (!window.confirm(`${changes.length} Zahlungs-Einträge bereinigen? Dies kann nicht rückgängig gemacht werden.`)) return;
    setBusy(true);
    try {
      const actions = changes.map(([payment_id, c]) => ({ payment_id, action: c.action, invoice_id: c.invoice_id }));
      const { data: res } = await api.post("/maintenance/payment-audit/apply", { actions });
      toast.success(`Bereinigt: ${res.removed_payments} Einträge, davon ${res.applied_to_invoices} Rechnungen zugeordnet`);
      await check();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bereinigung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-3" data-testid="payment-audit-section">
      <div>
        <div className="label-eyebrow flex items-center gap-1.5"><Broom size={14} /> Datenqualität</div>
        <h2 className="font-display text-2xl font-bold mt-1">Daten prüfen &amp; bereinigen</h2>
        <p className="text-sm text-[#5B6371] mt-1 max-w-3xl">
          Findet alte Zahlungs-Einträge ohne Rechnungs-Zuordnung, die Ihren IST-Umsatz doppelt zählen könnten.
          Sie sehen zuerst eine Vorschau und entscheiden pro Eintrag.
        </p>
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 space-y-4">
        <button type="button" onClick={check} disabled={busy}
          className="inline-flex items-center gap-2 bg-[#1F242E] hover:bg-black text-white text-sm font-medium rounded-md px-4 py-2.5 disabled:opacity-60"
          data-testid="audit-check-btn">
          <MagnifyingGlass size={14} /> {busy && !data ? "Prüfe …" : "Jetzt prüfen"}
        </button>

        {data && data.items.length === 0 && (
          <div className="text-sm text-[#0AA09E] font-medium" data-testid="audit-clean">Alles sauber — keine nicht zugeordneten Zahlungen gefunden.</div>
        )}

        {data && data.items.length > 0 && (
          <>
            <div className="text-sm text-[#1F242E]" data-testid="audit-summary">
              {data.summary.count} nicht zugeordnete Zahlungen · davon <strong className="text-[#FF6B5C]">{data.summary.duplicate_count} wahrscheinlich doppelt ({formatEUR(data.summary.duplicate_amount)})</strong>
              {" "}· {data.summary.apply_count} passen zu offenen Rechnungen
            </div>
            <div className="overflow-x-auto border border-[#E5E7EB] rounded-md">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead>
                  <tr className="text-xs uppercase tracking-widest text-[#5B6371] bg-[#F8FAFC] border-b border-[#E5E7EB]">
                    <th className="text-left px-3 py-2 font-medium">Zahlung</th>
                    <th className="text-left px-3 py-2 font-medium">Datum</th>
                    <th className="text-right px-3 py-2 font-medium">Betrag</th>
                    <th className="text-left px-3 py-2 font-medium">Passende Rechnung</th>
                    <th className="text-left px-3 py-2 font-medium">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it) => (
                    <AuditRow key={it.payment_id} it={it} choice={choices[it.payment_id]}
                      onChange={(c) => setChoices({ ...choices, [it.payment_id]: c })} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={apply} disabled={busy || !changes.length}
                className="px-5 py-2.5 bg-[#FF6B5C] hover:bg-[#e85a4c] text-white text-sm font-medium rounded-md disabled:opacity-50"
                data-testid="audit-apply-btn">
                {changes.length} Einträge bereinigen
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
